import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Schedule.css';
import BookingModal from '../booking/BookingModal';
import BookingSuccess from '../booking/BookingSuccess';
import ClassDetails from './ClassDetails';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

// which apis is this component using?
// - /api/classes/by-date
// - /api/classes/available-dates
// - /api/classes/

// Module-scoped in-memory cache
const inMemoryCache = {};

const Schedule = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [userBookedDates, setUserBookedDates] = useState([]);

  // Create ref for the cache
  const cacheRef = useRef(inMemoryCache);

  // New state for booking functionality
  const [selectedClass, setSelectedClass] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  
  // New state for class details modal
  const [showClassDetails, setShowClassDetails] = useState(false);
  const [classDetailsData, setClassDetailsData] = useState(null);
  
  // Language detection
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';
  
  // Add SEO metadata for Slovak language users
  useEffect(() => {
    if (isSlovak) {
      // Set page title for Schedule page
      const pageTitle = "Rozvrh jogy | TiDeaSolis Yoga Štúdio";
      document.title = pageTitle;
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', "Pozrite si náš aktuálny rozvrh jogy a rezervujte si svoje miesto na hodine. Prehliadajte hodiny podľa dátumu.");
      
      // Add Open Graph tags
      const updateMetaTag = (property, content) => {
        let metaTag = document.querySelector(`meta[property="${property}"]`);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('property', property);
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', content);
      };
      
      updateMetaTag('og:locale', 'sk_SK');
      updateMetaTag('og:title', pageTitle);
      updateMetaTag('og:description', "Pozrite si náš aktuálny rozvrh jogy a rezervujte si svoje miesto na hodine. Prehliadajte hodiny podľa dátumu.");
      updateMetaTag('og:type', 'website');
    }
    
    // Ensure Quicksand font is loaded
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700"]')) {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(fontLink);
    }
    
    // Load Cormorant Garamond font
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700"]')) {
      const garamondLink = document.createElement('link');
      garamondLink.rel = 'stylesheet';
      garamondLink.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(garamondLink);
    }
    
    // Add structured data for the schedule
    const generateScheduleSchema = () => {
      // Only generate if we have classes
      if (!classes.length) return null;
      
      const events = classes.map(yogaClass => {
        // Format the date
        const classDate = new Date(selectedDate);
        const [hours, minutes] = (yogaClass.start_time || '00:00').split(':').map(Number);
        classDate.setHours(hours, minutes, 0, 0);
        
        // End time calculation
        const endDate = new Date(classDate);
        const durationMinutes = yogaClass.duration || 60;
        endDate.setMinutes(endDate.getMinutes() + durationMinutes);
        
        return {
          "@type": "Event",
          "name": yogaClass.title || t('untitled_class'),
          "startDate": classDate.toISOString(),
          "endDate": endDate.toISOString(),
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": "TiDeaSolis Yoga Štúdio",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Šafárikova 37",
              "addressLocality": "Rožňava",
              "addressCountry": "SK"
            }
          },
          "performer": {
            "@type": "Person",
            "name": yogaClass.instructor || t('staff')
          },
          "organizer": {
            "@type": "Organization",
            "name": "TiDeaSolis Yoga Štúdio",
            "url": window.location.origin
          },
          "offers": {
            "@type": "Offer",
            "price": yogaClass.price !== undefined ? yogaClass.price : 0,
            "priceCurrency": "EUR",
            "availability": yogaClass.available_spots > 0 ? 
              "https://schema.org/InStock" : 
              "https://schema.org/SoldOut",
            "validFrom": new Date().toISOString()
          }
        };
      });
      
      const scheduleData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": events.map((event, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": event
        }))
      };
      
      return scheduleData;
    };
    
    // Add or update JSON-LD script for schedule
    const scheduleSchema = generateScheduleSchema();
    if (scheduleSchema) {
      let scriptTag = document.getElementById('schedule-schema');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'schedule-schema';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(scheduleSchema);
    }
    
    return () => {
      // Optional cleanup if component unmounts
    };
  }, [isSlovak, classes, selectedDate, t]);

  // Format date to YYYY-MM-DD string
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to check cache validity (5 minutes)
  const isCacheValid = (timestamp) => {
    const fiveMinutesMs = 5 * 60 * 1000; // 5 minutes in milliseconds
    return Date.now() - timestamp < fiveMinutesMs;
  };

  // Parse date string to Date object
  const parseDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Fetch available dates (dates with classes) for the current month
  useEffect(() => {
    const fetchAvailableDates = async () => {
      try {
        // Create a cache key for this specific request
        const cacheKey = `availableDates_${currentMonth}_${currentYear}`;
        const cachedData = cacheRef.current[cacheKey];
        
        if (cachedData) {
          const { data, timestamp } = cachedData;
          
          // If cache is still valid (less than 5 minutes old)
          if (isCacheValid(timestamp)) {
            // console.log('Using cached available dates data');
            
            // Parse the date strings to Date objects
            if (data && Array.isArray(data.dates_with_classes)) {
              const dates = data.dates_with_classes.map(dateStr => parseDate(dateStr));
              setAvailableDates(dates);
              return;
            }
          } else {
            // console.log('Cache expired, fetching fresh data');
          }
        }
        
        // If no valid cache exists, fetch from API
        const response = await axios.get(`classes/available-dates`, {
          params: {
            month: currentMonth,
            year: currentYear
          }
        });
        
        // console.log('Available dates response:', response.data);
        
        // Store the response in our in-memory cache with a timestamp
        cacheRef.current[cacheKey] = {
          data: response.data,
          timestamp: Date.now()
        };
        
        // Parse the date strings to Date objects
        if (response.data && Array.isArray(response.data.dates_with_classes)) {
          const dates = response.data.dates_with_classes.map(dateStr => parseDate(dateStr));
          setAvailableDates(dates);
        } else {
          // console.error('Unexpected format for available dates:', response.data);
          setAvailableDates([]);
        }
      } catch (err) {
        // console.error('Error fetching available dates:', err);
        setAvailableDates([]);
      }
    };

    fetchAvailableDates();
  }, [currentMonth, currentYear]);

  // Fetch classes for the selected date
  useEffect(() => {
    fetchClasses(selectedDate);
  }, [selectedDate]);

  // Fetch user's booked dates if logged in
  useEffect(() => {
    const fetchUserBookings = async () => {
      // Only fetch if user is logged in
      if (!currentUser) {
        setUserBookedDates([]);
        return;
      }

      try {
        const response = await axios.get('/bookings/user-bookings');
        
        if (response.data.success && response.data.bookings) {
          // Filter for only future confirmed bookings
          const now = new Date();
          const futureBookings = response.data.bookings.filter(booking => {
            const bookingDate = new Date(booking.start_time);
            return bookingDate > now && booking.status.toLowerCase() === 'confirmed';
          });
          
          // Extract unique dates from bookings
          const bookedDates = futureBookings.map(booking => {
            const date = new Date(booking.start_time);
            // Create a date object with just the date part (no time) for comparison
            return new Date(date.getFullYear(), date.getMonth(), date.getDate());
          });
          
          setUserBookedDates(bookedDates);
        }
      } catch (err) {
        // Silently fail - this is a non-critical feature
        console.error('Error fetching user bookings for calendar:', err);
        setUserBookedDates([]);
      }
    };

    fetchUserBookings();
  }, [currentUser]);

  // Helper function to check if a date is available (has classes)
  const isDateAvailable = (date) => {
    return availableDates.some(availableDate => 
      availableDate.getDate() === date.getDate() &&
      availableDate.getMonth() === date.getMonth() &&
      availableDate.getFullYear() === date.getFullYear()
    );
  };

  // Helper function to check if user has a booking on this date
  const isDateUserBooked = (date) => {
    return userBookedDates.some(bookedDate => 
      bookedDate.getDate() === date.getDate() &&
      bookedDate.getMonth() === date.getMonth() &&
      bookedDate.getFullYear() === date.getFullYear()
    );
  };

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Add cells for each day of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth - 1, i);
      const isAvailable = isDateAvailable(date);
      const isUserBooked = isDateUserBooked(date);
      const isSelected = selectedDate && 
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
      
      days.push(
        <div 
          key={`day-${i}`} 
          className={`calendar-day ${isAvailable ? 'available' : ''} ${isUserBooked ? 'user-booked' : ''} ${isSelected ? 'selected' : ''} ${isToday(date) ? 'today' : ''}`}
          onClick={() => setSelectedDate(date)}
        >
          {i}
        </div>
      );
    }
    
    return days;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Go to today's date
  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonth(today.getMonth() + 1);
    setCurrentYear(today.getFullYear());
  };

  // Get the month name
  const getMonthName = (month) => {
    const monthKey = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ][month - 1];
    return t(monthKey);
  };

  // Format time with 24-hour format
  const formatTime = (timeStr) => {
    try {
      if (!timeStr) return 'N/A';
      
      const [hours, minutes] = timeStr.split(':').map(Number);
      
      // Check if we have valid numbers
      if (isNaN(hours) || isNaN(minutes)) return timeStr;

      // Convert times to the user's local timezone if needed
      // Create a Date object for today with the given hours and minutes
      const date = new Date(selectedDate);
      date.setHours(hours, minutes, 0, 0);
      
      // Extract hours and minutes from the timezone-adjusted date
      const localHours = date.getHours();
      const localMinutes = date.getMinutes();
      
      // Format in 24-hour with leading zeros
      return `${localHours.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`;
    } catch (error) {
      // console.error('Error formatting time:', error);
      return timeStr || 'N/A';
    }
  };

  // Get difficulty text based on numerical value
  const getDifficultyText = (difficulty) => {
    if (!difficulty) return t('normal');
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return t('relax');
      case 2:
        return t('medium');
      case 3:
        return t('dynamic');
      default:
        return t('normal');
    }
  };

  // Get difficulty class
  const getDifficultyClass = (difficulty) => {
    if (!difficulty) return 'difficulty-unknown';
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return 'difficulty-beginner';
      case 2:
        return 'difficulty-intermediate';
      case 3:
        return 'difficulty-advanced';
      default:
        return 'difficulty-unknown';
    }
  };

  // Format date for display (e.g., "Monday, January 1, 2023")
  const formatDateForDisplay = (date) => {
    try {
      if (!date) return '';
      
      // Use toLocaleDateString with language-specific formatting instead of array lookup
      const options = { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      
      return date.toLocaleDateString(currentLanguage === 'sk' ? 'sk-SK' : 'en-US', options);
    } catch (error) {
      // console.error('Error formatting date for display:', error);
      return '';
    }
  };

  // Add this helper function to check if a class is a workshop
  const isWorkshop = (yogaClass) => {
    return yogaClass.workshop === 'yes';
  };

  // Add this function to get button state
  const getButtonState = (yogaClass) => {
    // Create a date object for the class
    const classDate = new Date(selectedDate); // Create a new Date object to avoid modification
    const [hours, minutes] = yogaClass.start_time.split(':');
    classDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
    
    // For workshops, we always enable booking
    if (isWorkshop(yogaClass)) {
      return { disabled: false, text: t('book_now') };
    }
    
    if (yogaClass.available_spots <= 0) return { disabled: true, text: t('class_full') };
    if (isClassStarted(yogaClass.start_time)) return { disabled: true, text: t('class_started') };
    if (isClassTooFarAhead(classDate)) return { disabled: true, text: t('booking_too_far_ahead') };
    if (yogaClass.instructor_status === 'inactive') return { disabled: true, text: t('class_not_bookable') };
    return { disabled: false, text: t('book_now') };
  };

  // Add this handler for showing class details
  const handleShowDetails = (yogaClass) => {
    setClassDetailsData(yogaClass);
    setShowClassDetails(true);
  };

  // Add these new functions for booking
  const handleBookNowClick = (yogaClass) => {
    // Create a new class object with the combined date and time
    const classDate = new Date(selectedDate); // Create a new Date object to avoid modifying selectedDate
    const [hours, minutes] = yogaClass.start_time.split(':').map(Number);
    
    // Set the hours and minutes on the selected date
    classDate.setHours(hours, minutes, 0, 0);
    
    // Create a new class object with the date_time property
    const classWithDateTime = {
      ...yogaClass,
      date_time: classDate.toISOString(), // This will include timezone offset
      // Add additional fields needed for workshop bookings
      start_time: classDate.toISOString(),
      instructor_bio: yogaClass.instructor_bio || '',
      benefits: yogaClass.benefits || ''
    };
    
    setSelectedClass(classWithDateTime);
    setShowBookingModal(true);
  };

  const handleCloseBookingModal = () => {
    setShowBookingModal(false);
    setSelectedClass(null);
  };

  const handleBookingSuccess = (data) => {
    setBookingSuccess(data);
    setShowBookingModal(false);
    
    // Clear the cache for this date to ensure fresh data after booking
    const dateStr = formatDateToString(selectedDate);
    delete cacheRef.current[`classes_${dateStr}`];
    
    // Refresh user bookings if user is logged in
    if (currentUser) {
      fetchUserBookingsForCalendar();
    }
  };

  const handleCloseSuccessModal = () => {
    setBookingSuccess(null);
    // Refresh classes to update available spots
    fetchClasses(selectedDate);
    // Refresh user bookings if user is logged in
    if (currentUser) {
      fetchUserBookingsForCalendar();
    }
  };

  // Helper function to fetch user bookings for calendar highlighting
  const fetchUserBookingsForCalendar = async () => {
    try {
      const response = await axios.get('/bookings/user-bookings');
      
      if (response.data.success && response.data.bookings) {
        // Filter for only future confirmed bookings
        const now = new Date();
        const futureBookings = response.data.bookings.filter(booking => {
          const bookingDate = new Date(booking.start_time);
          return bookingDate > now && booking.status.toLowerCase() === 'confirmed';
        });
        
        // Extract unique dates from bookings
        const bookedDates = futureBookings.map(booking => {
          const date = new Date(booking.start_time);
          // Create a date object with just the date part (no time) for comparison
          return new Date(date.getFullYear(), date.getMonth(), date.getDate());
        });
        
        setUserBookedDates(bookedDates);
      }
    } catch (err) {
      // Silently fail - this is a non-critical feature
      console.error('Error fetching user bookings for calendar:', err);
    }
  };

  // Update the fetchClasses function to be reusable
  const fetchClasses = async (date) => {
    setLoading(true);
    setError(null);
    
    try {
      const dateStr = formatDateToString(date);
      // console.log(`Schedule: Fetching classes for date: ${dateStr}`);
      
      // Check in-memory cache first
      const cacheKey = `classes_${dateStr}`;
      const cachedData = cacheRef.current[cacheKey];
      
      if (cachedData) {
        const { data, timestamp } = cachedData;
        
        // Use cached data if it's less than 5 minutes old
        if (isCacheValid(timestamp)) {
          // console.log('Schedule: Using cached class data');
          
          if (data && Array.isArray(data.classes)) {
            // Additional check to ensure we're only displaying classes for the selected date
            const selectedDateStr = formatDateToString(date);
            const filteredClasses = data.classes.filter(yogaClass => {
              // If the class has a date property, use it for filtering
              if (yogaClass.date) {
                return yogaClass.date === selectedDateStr;
              }
              // Otherwise assume it's for the correct date (as filtered by backend)
              return true;
            });
            
            setClasses(filteredClasses);
            setLoading(false);
            return;
          }
        } else {
          // console.log('Schedule: Cache expired, fetching fresh data');
        }
      }
      
      // If no valid cache exists, fetch from API
      const response = await axios.get(`classes/by-date`, {
        params: {
          date: dateStr
        }
      });
      
      // console.log('Schedule: API response:', response.data);
      
      // Store response in in-memory cache with timestamp
      cacheRef.current[cacheKey] = {
        data: response.data,
        timestamp: Date.now()
      };
      
      if (response.data && Array.isArray(response.data.classes)) {
        // Additional check to ensure we're only displaying classes for the selected date
        const selectedDateStr = formatDateToString(date);
        const filteredClasses = response.data.classes.filter(yogaClass => {
          // If the class has a date property, use it for filtering
          if (yogaClass.date) {
            return yogaClass.date === selectedDateStr;
          }
          // Otherwise assume it's for the correct date (as filtered by backend)
          return true;
        });
        
        // console.log(`Schedule: Filtered ${response.data.classes.length} to ${filteredClasses.length} classes for date ${selectedDateStr}`);
        setClasses(filteredClasses);
      } else {
        // console.error('Schedule: Unexpected response format:', response.data);
        setClasses([]);
        setError(t('failed_load_classes'));
      }
    } catch (err) {
      // console.error('Schedule: Error fetching classes:', err);
      setError(t('failed_load_classes_try_again'));
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function
  const isClassStarted = (startTime) => {
    const classTime = new Date(selectedDate);
    const [hours, minutes] = startTime.split(':');
    classTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
    
    // Important: We don't need to do any additional timezone conversion
    // since both classTime and now are in the same local timezone
    const now = new Date();
    return now >= classTime;
  };

  // Add this helper function to check if a class is more than 21 days in the future
  const isClassTooFarAhead = (classDate) => {
    const now = new Date();
    const maxBookingWindow = new Date(now);
    maxBookingWindow.setDate(maxBookingWindow.getDate() + 21); // 21 days from now
    return classDate > maxBookingWindow;
  };

  // Add this handler for closing class details
  const handleCloseClassDetails = () => {
    setShowClassDetails(false);
    setClassDetailsData(null);
  };

  return (
    <div className="schedule-container" lang={currentLanguage} itemScope itemType="https://schema.org/ScheduleAction">
      <h1 itemProp="name">{t('class_schedule')}</h1>
      
      <div className="schedule-content">
        <div className="schedule-calendar-section">
          <div className="calendar-controls">
            <button onClick={goToPreviousMonth} className="calendar-control-btn" aria-label={isSlovak ? "Predchádzajúci mesiac" : "Previous month"}>&laquo;</button>
            <h2 itemProp="scheduledTime">{getMonthName(currentMonth)} {currentYear}</h2>
            <button onClick={goToNextMonth} className="calendar-control-btn" aria-label={isSlovak ? "Nasledujúci mesiac" : "Next month"}>&raquo;</button>
            <button onClick={goToToday} className="today-btn" lang={currentLanguage}>{t('today')}</button>
          </div>
          
          <div className="calendar">
            <div className="calendar-header">
              <div lang={currentLanguage}>{t('sun')}</div>
              <div lang={currentLanguage}>{t('mon')}</div>
              <div lang={currentLanguage}>{t('tue')}</div>
              <div lang={currentLanguage}>{t('wed')}</div>
              <div lang={currentLanguage}>{t('thu')}</div>
              <div lang={currentLanguage}>{t('fri')}</div>
              <div lang={currentLanguage}>{t('sat')}</div>
            </div>
            <div className="calendar-grid">
              {generateCalendarDays()}
            </div>
          </div>
          
          <div className="selected-date-info">
            <h3>
              {(() => {
                try {
                  // Format the selected date properly using Intl.DateTimeFormat
                  const locale = currentLanguage === 'sk' ? 'sk-SK' : 'en-US';
                  const formatter = new Intl.DateTimeFormat(locale, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                  return formatter.format(selectedDate);
                } catch (error) {
                  // console.error('Error formatting date:', error);
                  return '';
                }
              })()}
            </h3>
          </div>
        </div>
        
        <div className="schedule-classes-section">
          <div className="classes-container">
            {error ? (
              <div className="error" role="alert" lang={currentLanguage}>{error}</div>
            ) : classes.length > 0 ? (
              classes.map((yogaClass, index) => (
                <div key={yogaClass.id || index} className="class-card" lang={currentLanguage} itemScope itemType="https://schema.org/Event">
                  <meta itemProp="name" content={yogaClass.title || t('untitled_class')} />
                  <meta itemProp="startDate" content={`${formatDateToString(selectedDate)}T${yogaClass.start_time || '00:00'}`} />
                  <meta itemProp="eventStatus" content="https://schema.org/EventScheduled" />
                  
                  <div className="class-image">
                    <img 
                      src={yogaClass.instructor_image || '/images/defaults/instructor-placeholder.jpg'} 
                      alt={yogaClass.instructor || t('staff')} 
                      itemProp="image"
                    />
                  </div>
                  
                  <div className="class-content">
                    <h4 className="class-title" itemProp="name">
                      {yogaClass.title || t('untitled_class')} 
                    </h4>
                    
                    <div className="class-time-range">
                      <span className="time-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <div className="time-values" itemProp="startDate">
                        {formatTime(yogaClass.start_time)} - {formatTime(yogaClass.end_time)}
                      </div>
                    </div>
                    
                    <div className="instructor-name" itemProp="performer" itemScope itemType="https://schema.org/Person">
                      <span itemProp="name">{yogaClass.instructor || t('staff')}</span>
                    </div>
                    
                    <button 
                      className="show-details-button" 
                      onClick={() => handleShowDetails(yogaClass)}
                      aria-label={isSlovak ? `Zobraziť detaily pre ${yogaClass.title || t('untitled_class')}` : `Show details for ${yogaClass.title || t('untitled_class')}`}
                      lang={currentLanguage}
                    >
                      {t('show_details')} <span className="arrow">{String.fromCharCode(8250)}</span>
                    </button>
                  </div>
                  
                  <div className="class-actions">
                    <div className="class-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                      <meta itemProp="priceCurrency" content="EUR" />
                      <span itemProp="price">{yogaClass.price !== undefined ? yogaClass.price : 0}</span>{' '}
                      {String.fromCharCode(8364)}
                    </div>
                    
                    <div className="reservation-container">
                      <button
                        className="book-now-button"
                        onClick={() => handleBookNowClick(yogaClass)}
                        disabled={getButtonState(yogaClass).disabled}
                        aria-label={isSlovak ? `Rezervovať ${yogaClass.title || t('untitled_class')}` : `Reserve ${yogaClass.title || t('untitled_class')}`}
                        lang={currentLanguage}
                      >
                        {t('reserve')}
                      </button>
                      
                      <div className="availability-container">
                        <div className="class-capacity">
                          <div className="capacity-bar">
                            <div 
                              className="capacity-filled" 
                              style={{ 
                                width: `${Math.min(100, Math.max(0, 
                                  ((yogaClass.max_capacity || 10) - (yogaClass.available_spots || 0)) / 
                                  (yogaClass.max_capacity || 10) * 100
                                ))}%` 
                              }}
                              aria-hidden="true"
                            ></div>
                          </div>
                         
                          <span lang={currentLanguage}>{t('free_spots')} {yogaClass.available_spots || 0}</span>
                        </div>
                        <span className={`difficulty-tag ${getDifficultyClass(yogaClass.difficulty)}`} lang={currentLanguage}>
                          {getDifficultyText(yogaClass.difficulty)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-classes" lang={currentLanguage}>
                <p>{t('no_classes_scheduled')}</p>
                <p>{t('try_selecting_date')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Booking Modal */}
      {showBookingModal && selectedClass && (
        <BookingModal 
          yogaClass={selectedClass}
          onClose={handleCloseBookingModal}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Booking Success Modal */}
      {bookingSuccess && (
        <BookingSuccess 
          bookingData={bookingSuccess}
          onClose={handleCloseSuccessModal}
        />
      )}
      
      {/* Class Details Modal */}
      {showClassDetails && classDetailsData && (
        <ClassDetails 
          yogaClass={classDetailsData}
          onClose={handleCloseClassDetails}
        />
      )}
    </div>
  );
};

export default Schedule; 